/**
 * Language Detection Utility
 *
 * Detects the user's preferred language from URL query parameters
 * and provides utilities for language-related operations.
 */

export type SupportedLanguage = "en" | "es" | "fr";

/**
 * Detects language from URL query parameters
 *
 * Supports:
 * - lang=es → Spanish
 * - lang=es-US → Spanish
 * - lang=es-MX → Spanish
 * - lang=en → English
 * - lang=fr → French
 * - lang=fr-FR → French
 * - lang=fr-CA → French
 * - No lang parameter → English (default)
 *
 * @returns The detected language code ('en', 'es', or 'fr')
 */
export function detectLanguageFromUrl(): SupportedLanguage {
	// Check if we're in a browser environment
	if (typeof window === "undefined") {
		return "en"; // Default for SSR
	}

	try {
		const urlParams = new URLSearchParams(window.location.search);
		const langParam = urlParams.get("lang");

		if (!langParam) {
			return "en"; // Default when no lang parameter
		}

		// Normalize to lowercase for comparison
		const normalizedLang = langParam.toLowerCase().trim();

		// Support Spanish variants (es, es-US, es-MX, etc.)
		if (normalizedLang.startsWith("es")) {
			return "es";
		}

		// Support French variants (fr, fr-FR, fr-CA, etc.)
		if (normalizedLang.startsWith("fr")) {
			return "fr";
		}

		// Support English variants (en, en-US, en-GB, etc.)
		if (normalizedLang.startsWith("en")) {
			return "en";
		}

		// Default fallback for unsupported languages
		return "en";
	} catch (error) {
		console.warn("Error detecting language from URL:", error);
		return "en"; // Safe fallback
	}
}

/**
 * Gets the browser's preferred language as a fallback
 *
 * @returns The browser's language preference ('en', 'es', or 'fr')
 */
export function getBrowserLanguage(): SupportedLanguage {
	if (typeof navigator === "undefined") {
		return "en"; // Default for SSR
	}

	try {
		// Get browser language preference
		const browserLang = navigator.language || navigator.languages?.[0] || "en";
		const normalizedLang = browserLang.toLowerCase();

		// Check if browser prefers Spanish
		if (normalizedLang.startsWith("es")) {
			return "es";
		}

		// Check if browser prefers French
		if (normalizedLang.startsWith("fr")) {
			return "fr";
		}

		return "en"; // Default to English
	} catch (error) {
		console.warn("Error detecting browser language:", error);
		return "en";
	}
}

/**
 * Detects language with fallback strategy:
 * 1. URL parameter (highest priority)
 * 2. Browser language (fallback)
 * 3. English (final fallback)
 *
 * @returns The detected language code
 */
export function detectLanguageWithFallback(): SupportedLanguage {
	// First try URL parameter
	const urlLang = detectLanguageFromUrl();

	// If URL doesn't specify language, check browser
	if (urlLang === "en") {
		const urlParams = new URLSearchParams(window.location.search);
		const hasLangParam = urlParams.has("lang");

		// Only use browser language if no lang parameter was specified
		if (!hasLangParam) {
			return getBrowserLanguage();
		}
	}

	return urlLang;
}

/**
 * Checks if a language code is supported
 *
 * @param lang - The language code to check
 * @returns True if the language is supported
 */
export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
	return lang === "en" || lang === "es" || lang === "fr";
}

/**
 * Gets the display name for a language code
 *
 * @param lang - The language code
 * @returns The display name of the language
 */
export function getLanguageDisplayName(lang: SupportedLanguage): string {
	switch (lang) {
		case "es":
			return "Español";
		case "fr":
			return "Français";
		default:
			return "English";
	}
}

/**
 * Gets the locale identifier for Lingui
 *
 * @param lang - The language code
 * @returns The locale identifier for Lingui
 */
export function getLanguageLocale(lang: SupportedLanguage): string {
	return lang; // For now, language codes match locale identifiers
}
